/**
 * Rasterises a PDF page to a PNG data URL.
 *
 * The logsheet validator shows its attachment in the Lightbox, which is an `<img>`, so a PDF
 * logsheet cannot be displayed directly. Content that arrives by email is already converted
 * server-side (`convertPdfToPng` in the backend stores page one as a PNG and records
 * `fileType: ".png"`), but content uploaded through the UI keeps `fileType: ".pdf"` and its
 * original bytes. Rendering page one here makes both routes look the same to the validator.
 *
 * `pdfjs` is imported lazily so that opening a logsheet does not pull the PDF engine into the
 * validator's bundle, and so this module stays importable outside the browser.
 */

/**
 * Longest-side pixel budget for the rendered page.
 *
 * Matches `maxContentImageDimension` in the backend, which is what `pdftoppm -scale-to` is
 * given on the email path, so a UI-uploaded logsheet renders at the same resolution as an
 * emailed one. A PDF page is measured in points (72 per inch), so rendering at scale 1 would
 * put a letter-size sheet at 792px on its long edge - too coarse to read handwriting once
 * zoomed - which is why this scales up as well as down.
 */
export const MAX_LOGSHEET_RASTER_DIMENSION = 2000;

/**
 * @returns true when the content's `fileType` is a PDF and therefore needs rasterising before
 * it can be shown in an `<img>`.
 */
export const isPdfFileType = (fileType?: string): boolean => fileType?.toLowerCase() === ".pdf";

/**
 * Scale factor that fits the longest edge of a page into `maxDimension`.
 *
 * Falls back to 1 for page or budget dimensions that cannot produce a usable canvas, so a
 * malformed page yields a small render rather than a zero-sized or absurdly large one.
 */
export const computeRasterScale = (pageWidth: number, pageHeight: number, maxDimension: number): number => {
  const longestSide = Math.max(pageWidth, pageHeight);
  if (!Number.isFinite(longestSide) || longestSide <= 0) return 1;
  if (!Number.isFinite(maxDimension) || maxDimension <= 0) return 1;
  return maxDimension / longestSide;
};

/**
 * Renders the first page of a PDF to a PNG data URL.
 *
 * Only page one is rendered, matching the email ingest path, which has always stored page one
 * as the logsheet image.
 *
 * @param data the PDF bytes
 * @param maxDimension longest-side pixel budget for the output
 * @throws if the PDF cannot be parsed or the page cannot be rendered
 */
export const renderPdfFirstPageToDataUrl = async (
  data: ArrayBuffer,
  maxDimension: number = MAX_LOGSHEET_RASTER_DIMENSION
): Promise<string> => {
  const { pdfjs } = await import("./pdfWorker");

  const pdf = await pdfjs.getDocument({ data: new Uint8Array(data) }).promise;
  try {
    const page = await pdf.getPage(1);
    const unscaled = page.getViewport({ scale: 1 });
    const viewport = page.getViewport({ scale: computeRasterScale(unscaled.width, unscaled.height, maxDimension) });

    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(viewport.width));
    canvas.height = Math.max(1, Math.round(viewport.height));

    const context = canvas.getContext("2d");
    if (!context) throw new Error("Could not get a 2d canvas context to render the PDF");

    // Unpainted areas of a PDF page are transparent, and the Lightbox canvas behind it is
    // black - without an opaque background, black text would land on black.
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, canvas.width, canvas.height);

    await page.render({ canvasContext: context, viewport }).promise;
    return canvas.toDataURL("image/png");
  } finally {
    await pdf.destroy();
  }
};
